import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Topbar from "./Topbar";
import { createClient } from "@supabase/supabase-js";
import Loader from "./Loader";
import FeaturedMatchupCard from "./FeaturedMatchupCard";
import Footer from "./Footer";

const apiBase = process.env.REACT_APP_API_URL;

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

const roleOrder = ["Top", "Jng", "Mid", "Bot", "Sup"];

const MatchupPage = () => {
  const { leagueId } = useParams();
  const [token, setToken] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [standings, setStandings] = useState([]);
  const [matchups, setMatchups] = useState([]);
  const [matchupDetails, setMatchupDetails] = useState(null);
  const [matchBreakdown, setMatchBreakdown] = useState({ home: [], away: [] });
  const [weekStart, setWeekStart] = useState(null);
  const [weekEnd, setWeekEnd] = useState(null);
  const [loading, setLoading] = useState(true);

//   const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  // ---------------------
  // Fetch matchup details
  // ---------------------
  const fetchMatchupDetails = useCallback(
    async (matchupId) => {
      try {
        const { data } = await axios.post(
          `${apiBase}/api/matchup/details`,
          { matchup_id: matchupId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMatchupDetails(data);
      } catch (err) {
        console.error("Error fetching matchup details:", err.message);
      }
    },
    [token]
  );

  // ---------------------
  // Fetch both team breakdowns
  // ---------------------
  const fetchBothBreakdowns = useCallback(
    async (homeTeamId, awayTeamId, start, end) => {
      try {
        const [homeRes, awayRes] = await Promise.all([
          axios.post(
            `${apiBase}/api/fantasy/team/match-breakdown`,
            { team_id: homeTeamId, start_date: start, end_date: end },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.post(
            `${apiBase}/api/fantasy/team/match-breakdown`,
            { team_id: awayTeamId, start_date: start, end_date: end },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);

        setMatchBreakdown({
          home: homeRes.data || [],
          away: awayRes.data || [],
        });
      } catch (err) {
        console.error("Error loading breakdown:", err);
        setMatchBreakdown({ home: [], away: [] });
      }
    },
    [token]
  );

  // ---------------------
  // Load dashboard once
  // ---------------------
  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError || !session?.user) {
          console.error("User not logged in or session expired:", sessionError?.message);
          await supabase.auth.signOut();
          window.location.href = "/login";
          return;
        }

        const token = session.access_token;
        setToken(token);

        const { data } = await axios.post(
          `${apiBase}/api/dashboard/data`,
          { league_id: leagueId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setTournament(data.league);
        setStandings(data.standings);
        setMatchups(data.mergedMatchups);
      } catch (err) {
        console.error("Error loading dashboard:", err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [leagueId]);

  // ---------------------
  // Determine featured matchup
  // ---------------------
  const featured = useMemo(() => {
    if (!matchups || matchups.length === 0) return null;

    const todayUtc = Date.now(); // use actual current time

    // first matchup whose week_end_at is after now
    const nextMatchup = matchups.find((m) => new Date(m.week_end_at).getTime() > todayUtc);

    if (nextMatchup) {
      const weekNumber = matchups.findIndex((m) => m.id === nextMatchup.id) + 1;
      return { ...nextMatchup, weekNumber };
    }

    // fallback: last matchup
    const lastIndex = matchups.length - 1;
    return { ...matchups[lastIndex], weekNumber: lastIndex + 1 };
  }, [matchups]);

  // ---------------------
  // Fetch matchup details when featured changes
  // ---------------------
  useEffect(() => {
    if (featured?.id && token) {
      fetchMatchupDetails(featured.id);
      setWeekStart(featured.week_start);
      setWeekEnd(featured.week_end);
    }
  }, [featured?.id, featured?.week_start, featured?.week_end, token, fetchMatchupDetails]);

  // ---------------------
  // Fetch breakdowns when matchup details are ready
  // ---------------------
  useEffect(() => {
    if (token && matchupDetails && weekStart && weekEnd) {
      fetchBothBreakdowns(matchupDetails.home_team_id, matchupDetails.away_team_id, weekStart, weekEnd);
    }
  }, [token, matchupDetails, weekStart, weekEnd, fetchBothBreakdowns]);

  // ---------------------
  // Memoized helpers
  // ---------------------
  const isLiveWeek = useCallback((start, end) => {
    const now = new Date();
    return now >= new Date(start) && now <= new Date(end);
  }, []);

  const getTeamRecord = useCallback(
    (teamId) => {
      const record = standings.find((s) => s.fantasy_team_id === teamId);
      if (!record) return "0W - 0L";
      return `${record.wins}W - ${record.losses}L${record.ties ? ` - ${record.ties}T` : ""}`;
    },
    [standings]
  );

  const getProjectedWinner = useCallback((match) => {
    if (!match?.home_team || !match?.away_team) return { winner: null, homeTotal: 0, awayTotal: 0 };

    const sumProjected = (team) => team?.user_fantasy_team_players?.reduce((total, p) => total + (p.h2h_projected || 0), 0) || 0;

    const homeTotal = sumProjected(match.home_team);
    const awayTotal = sumProjected(match.away_team);

    let winner = "tie";
    if (homeTotal > awayTotal) winner = "home";
    else if (awayTotal > homeTotal) winner = "away";

    return { winner, homeTotal, awayTotal };
  }, []);

  const live = featured ? isLiveWeek(featured.week_start, featured.week_end) : false;

  if (loading) return <Loader loadingTitle="Loading Arena" showLogo />;

  return (
    <div className="page-container">
      <Topbar leagueId={leagueId} showBudget showLinks />
      <div className="main">
        <FeaturedMatchupCard
          featured={featured}
          tournament={tournament}
          roleOrder={roleOrder}
          live={live}
          matchBreakdown={matchBreakdown.home}
          matchBreakdownAway={matchBreakdown.away}
          matchupDetails={matchupDetails}
          getTeamRecord={getTeamRecord}
          getProjectedWinner={getProjectedWinner}
        />
      </div>
      <Footer />
    </div>
  );
};

export default MatchupPage;
